/**
 * Top navigation bar
 * Shows user info from Convex Auth and sign out option
 * 
 * Per design.md: Use semantic tokens, @uprosal/ui components, lucide icons only
 */

import { useState, useRef, useEffect } from 'react';
import { Bell, ChevronDown, LogOut, Settings, Crown, Coins, Check, AlertTriangle, Sparkles, Info } from '@uprosal/ui';
import { Button } from '@uprosal/ui';
import { useQuery, useMutation } from 'convex/react';
import { useAuthActions } from '@convex-dev/auth/react';
import { api } from 'convex/_generated/api';
import type { Doc } from 'convex/_generated/dataModel';
import { Link } from 'react-router-dom';
import { SearchBar } from '../components/SearchBar';

/** Format token count for display */
function formatTokens(tokens: number): string {
    if (tokens >= 1_000_000) {
        return `${(tokens / 1_000_000).toFixed(1)}M`;
    }
    if (tokens >= 1_000) {
        return `${(tokens / 1_000).toFixed(1)}K`;
    }
    return tokens.toString();
}

export function TopNav() {
    const currentUser = useQuery(api.onboarding.getCurrentUser);
    const usage = useQuery(api.queries.billing.getUsage, { range: "month" });
    const notificationsData = useQuery(api.queries.notifications.listRecent, { limit: 5 });
    const markAllAsRead = useMutation(api.mutations.notifications.markAllAsRead);
    const { signOut } = useAuthActions();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const notifRef = useRef<HTMLDivElement>(null);

    // Close menus on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
            if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
                setShowNotifications(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSignOut = () => {
        void signOut();
    };

    const handleMarkAllAsRead = () => {
        void markAllAsRead({});
    };

    // Calculate total tokens used this month
    type UsageEvent = Doc<'usageEvents'>;
    const usageEvents = (usage?.usageEvents ?? []) as UsageEvent[];
    const totalTokens = usageEvents.reduce((sum, e) => {
        return sum + (e.tokensIn || 0) + (e.tokensOut || 0);
    }, 0);

    const userRole = currentUser?.team?.role;
    const isOwnerOrAdmin = userRole === 'owner' || userRole === 'admin';

    type NotificationItem = {
        id: string;
        type: string;
        title: string;
        message: string;
        isRead: boolean;
        timeAgo: string;
    };
    const unreadCount = notificationsData?.unreadCount ?? 0;
    const notifications = (notificationsData?.notifications ?? []) as NotificationItem[];

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'usage_alert': return AlertTriangle;
            case 'new_feature': return Sparkles;
            case 'tip': return Info;
            default: return Bell;
        }
    };

    return (
        <header className="h-16 shrink-0 border-b border-border bg-card/50 backdrop-blur-sm px-6 flex items-center justify-between">
            {/* Search - add left padding on mobile for hamburger menu */}
            <div className="flex items-center flex-1 max-w-md ml-12 md:ml-0">
                <SearchBar />
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
                {/* Notifications */}
                <div className="relative" ref={notifRef}>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="relative"
                        onClick={() => setShowNotifications(!showNotifications)}
                    >
                        <Bell className="w-5 h-5 text-muted-foreground" />
                        {unreadCount > 0 && (
                            <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                        )}
                    </Button>

                    {/* Notifications dropdown */}
                    {showNotifications && (
                        <div className="absolute right-0 mt-2 w-80 rounded-lg border border-border bg-popover shadow-lg py-2 z-50">
                            <div className="px-4 py-2 border-b border-border flex items-center justify-between">
                                <p className="text-sm font-medium text-foreground">Notifications</p>
                                {unreadCount > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 text-xs"
                                        onClick={handleMarkAllAsRead}
                                    >
                                        <Check className="w-3 h-3 mr-1" />
                                        Mark all read
                                    </Button>
                                )}
                            </div>

                            {notifications.length === 0 ? (
                                <div className="px-4 py-8 text-center">
                                    <Bell className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                                    <p className="text-sm text-muted-foreground">No notifications yet</p>
                                </div>
                            ) : (
                                <div className="max-h-80 overflow-y-auto">
                                    {notifications.map((notif) => {
                                        const Icon = getNotificationIcon(notif.type);
                                        return (
                                            <div
                                                key={notif.id}
                                                className={`px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer ${!notif.isRead ? 'bg-primary/5' : ''
                                                    }`}
                                            >
                                                <div className="flex gap-3">
                                                    <Icon className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-foreground">
                                                            {notif.title}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground line-clamp-2">
                                                            {notif.message}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground/70 mt-1">
                                                            {notif.timeAgo}
                                                        </p>
                                                    </div>
                                                    {!notif.isRead && (
                                                        <span className="w-2 h-2 bg-primary rounded-full shrink-0" />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="px-4 py-2 border-t border-border">
                                <Link
                                    to="/settings#notifications"
                                    className="text-xs text-primary hover:underline"
                                    onClick={() => setShowNotifications(false)}
                                >
                                    Notification settings
                                </Link>
                            </div>
                        </div>
                    )}
                </div>

                {/* User menu */}
                {currentUser ? (
                    <div className="relative" ref={menuRef}>
                        <Button
                            variant="ghost"
                            onClick={() => setShowUserMenu(!showUserMenu)}
                            className="flex items-center gap-2 px-2"
                        >
                            {currentUser.image ? (
                                <img
                                    src={currentUser.image}
                                    alt={currentUser.name || 'User'}
                                    className="w-8 h-8 rounded-full"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                                    <span className="text-sm font-medium text-primary-foreground">
                                        {(currentUser.name || currentUser.email || 'U')[0].toUpperCase()}
                                    </span>
                                </div>
                            )}
                            <span className="text-sm font-medium text-foreground hidden sm:block">
                                {currentUser.name || currentUser.email?.split('@')[0]}
                            </span>
                            <ChevronDown className="w-4 h-4 text-foreground" />
                        </Button>

                        {/* Dropdown menu */}
                        {showUserMenu && (
                            <div className="absolute right-0 mt-2 w-64 rounded-lg border border-border bg-popover shadow-lg py-2 z-50">
                                <div className="px-4 py-2 border-b border-border">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium text-foreground">
                                            {currentUser.name || 'User'}
                                        </p>
                                        {isOwnerOrAdmin && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                                <Crown className="w-3 h-3" />
                                                {userRole === 'owner' ? 'Owner' : 'Admin'}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {currentUser.email}
                                    </p>
                                    {currentUser.team && (
                                        <p className="text-xs text-primary mt-1">
                                            {currentUser.team.name}
                                        </p>
                                    )}
                                </div>

                                {/* Token usage section */}
                                <div className="px-4 py-2 border-b border-border">
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <Coins className="w-3.5 h-3.5" />
                                        <span>Tokens used this month:</span>
                                        <span className="font-medium text-foreground ml-auto">
                                            {formatTokens(totalTokens)}
                                        </span>
                                    </div>
                                    {isOwnerOrAdmin && (
                                        <p className="text-xs text-success mt-1">
                                            Unlimited usage (Owner)
                                        </p>
                                    )}
                                </div>

                                <Link
                                    to="/settings"
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                                    onClick={() => setShowUserMenu(false)}
                                >
                                    <Settings className="w-4 h-4" />
                                    Settings
                                </Link>
                                <button
                                    onClick={handleSignOut}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-destructive hover:bg-muted transition-colors w-full"
                                >
                                    <LogOut className="w-4 h-4" />
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
                )}
            </div>
        </header>
    );
}
